import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Minimize } from "lucide-react";
import { linkUrlOf } from "../../lib/linkPlayer";
import { linkItemOf } from "../../lib/castQueue";
import {
  IDLE_HANDLE, PlayableMedia, playerKey, resolveItem, type MediaEvent, type MediaHandle, type PlayableItem, type ResolvedItem,
} from "../../player";
import { useCastFollower, type CastDriver, type KeyedDriver } from "../../hooks/useCastFollower";
import type { PlayerState } from "../../lib/castSync/castFollower";
import { serverClock } from "../../lib/castSync/serverClock";
import { positionAt, timelineOf } from "../../lib/castSync/timeline";
import type { StreamSlot } from "../../types/stream";
import { useElementFullscreen } from "./wallFullscreen";
import FullScreenPlayerControls from "../../player/controls/FullScreenPlayerControls";
import type { ControlButton, PlayerControlsSpec } from "../../player/controls/playerControlsSpec";
import { usePlayerControlsVisibility } from "../../player/controls/usePlayerControlsVisibility";
import type { CastFullScreen } from "./castControlsSpec";

/** How often an element's position goes to the follower (the file cast's rate). */
const REPORT_MS = 250;

interface Props {
  slot: StreamSlot;
  /** The item the slot plays now (CastDockCard: linkItemOf with the queue); else read from the slot alone. */
  item?: PlayableItem;
  /** Its kind and canPlay, resolved once by the card. */
  resolved?: ResolvedItem;
  /** The item's title (from the queue): an audio item's face shows it, else the kind's name. */
  title?: string | null;
  /** Your own cast only: the player is stuck (useCastFollower `onStall`). */
  onStall?: (positionMs: number) => void;
  /** Every position report of the current media. */
  onPosition?: (positionMs: number, state: PlayerState) => void;
  /** Your own cast only: the player finished this media. */
  onEnded?: (url: string) => void;
  /** Your own cast only: this media cannot play (the provider refused it, it failed to load, no hosted player). */
  onLoadError?: (url: string) => void;
  /** The dock's mini player: fill the 200×200 box (YouTube's minimum player size), no name line. */
  fill?: boolean;
  /** Full screen: the card's controls (castControlsSpec), in the band under the player. Without it: exit and the name. */
  fullScreen?: CastFullScreen;
}

/**
 * A link cast's player, for every item kind (spec 2026-10-19 §4.1, 2026-10-24 link kinds): the player module's
 * PlayableMedia, synced to the server timeline. A timed item (`caps.seekable`: YouTube, SoundCloud, an audio file)
 * loads paused at the timeline position; once loaded the cast follower (useCastFollower) seeks, starts at the
 * scheduled time and corrects drift through the media handle. A live item (radio) plays while the cast plays, from
 * the live edge; a photo just shows. Hosted embeds are mounted into our own div (never an iframe of ours: YouTube's
 * embed policy); provider taps never reach the frame (the hosted player's shield).
 */
export default function VmplLinkPlayer(props: Props) {
  const url = linkUrlOf(props.slot);
  if (!url) return null;
  const item = { ...(props.item ?? linkItemOf(props.slot, null)), url, title: props.title ?? props.item?.title ?? null };
  const resolved = props.resolved ?? resolveItem(item);
  // A new player when the rules change (embed ↔ audio, live ↔ timed: a late joiner's MUSIC slot plays live until the
  // queue says AUDIO_URL); the same one across items otherwise (the hosted player loads the next link).
  return <CastItemPlayer key={playerKey(resolved.kind)} {...props} item={item} resolved={resolved} url={url} />;
}

function CastItemPlayer({ slot, item, resolved, url, onStall, onPosition, onEnded, onLoadError, fill = false, fullScreen }: Props & { item: PlayableItem; resolved: ResolvedItem; url: string }) {
  const { t } = useTranslation();
  const { kind } = resolved;
  // Timed media follows the timeline; live media and photos get no driver (no seek, no drift sync, no ENDED).
  const timed = kind.caps.seekable;
  const live = kind.caps.live;
  const followerKind = kind.surface === "embed" ? "link" : "file";
  const media = useRef<MediaHandle>(IDLE_HANDLE);
  const slotRef = useRef(slot);
  slotRef.current = slot;

  // Our 16:9 box around the player; fullscreen goes on it (LiveStreamPlayer's pattern). Its own document: the card may
  // live in a Document PiP window.
  const [frameEl, setFrameEl] = useState<HTMLDivElement | null>(null);
  const fs = useElementFullscreen(frameEl);
  const isFullscreen = fs.on;
  // Embed rule: nothing of ours over the hosted player; the band under it auto-hides (a tap on the black shows it).
  const vis = usePlayerControlsVisibility({ active: isFullscreen, itemKey: url });
  const { hold } = vis;
  const fsPaused = isFullscreen && slot.playStatus !== "PLAY";
  const fsSheet = isFullscreen && fullScreen?.dialog != null;
  useEffect(() => hold("paused", fsPaused), [hold, fsPaused]);
  useEffect(() => hold("sheet", fsSheet), [hold, fsSheet]);
  const onFsChange = useRef(fullScreen?.onChange);
  onFsChange.current = fullScreen?.onChange;
  useEffect(() => onFsChange.current?.(isFullscreen), [isFullscreen]);
  // Unmounted in full screen (another item kind took the card): the card gets its own controls back.
  useEffect(() => () => onFsChange.current?.(false), []);

  const cbs = useRef({ onEnded, onLoadError });
  cbs.current = { onEnded, onLoadError };
  const [failed, setFailed] = useState(false);
  // R22: the timeline the follower held this media at its end for; a new timeline clears it.
  const timelineKey = `${slot.timelineRev}|${slot.playStatus}|${slot.seekTime}|${slot.playStartTime}`;
  const [heldFor, setHeldFor] = useState<string | null>(null);
  const heldEnd = heldFor === timelineKey;
  const [driver, setDriver] = useState<KeyedDriver | null>(null);
  const loadedKey = useRef<string | null>(null);
  const lastState = useRef<PlayerState | null>(null);
  // Stable identity: always talks to the current player. Elements also take a playback rate (drift nudging).
  const castDriver = useMemo<CastDriver>(
    () => ({
      seek: (ms) => media.current.seek(ms),
      play: () => media.current.play(),
      pause: () => media.current.pause(),
      ...(followerKind === "file" ? { setRate: (rate: number) => media.current.setRate(rate) } : {}),
    }),
    [followerKind],
  );
  const onHeldEnd = () => {
    setHeldFor(timelineKey);
    if (loadedKey.current) cbs.current.onEnded?.(loadedKey.current); // your own cast advances
  };
  const { report, resync } = useCastFollower(slot, followerKind, timed ? driver : null, timed ? url : null, {
    onStall: timed ? onStall : undefined,
    onPosition,
    onEnded: onHeldEnd,
  });
  const reportRef = useRef(report);
  reportRef.current = report;

  // New media: nothing of the last one holds.
  useEffect(() => {
    setDriver(null);
    setHeldFor(null);
    setFailed(false);
    loadedKey.current = null;
    lastState.current = null;
  }, [url]);

  const onEvent = (e: MediaEvent) => {
    // An element is sampled on a timer (below); embeds report by event.
    const sampled = media.current.sample() != null;
    if (e.type === "loaded") {
      loadedKey.current = e.key;
      setFailed(false);
      if (timed) setDriver({ key: e.key, driver: castDriver, durationMs: e.durationMs });
    } else if (e.type === "state") {
      lastState.current = e.state;
      if (!sampled) reportRef.current(e.key, e.positionMs, e.state);
    } else if (e.type === "position") {
      if (!sampled) reportRef.current(e.key, e.positionMs, lastState.current ?? "playing");
    } else if (e.type === "ended") {
      if (kind.caps.endsByItself) cbs.current.onEnded?.(e.key);
    } else if (e.type === "error") {
      setFailed(true);
      cbs.current.onLoadError?.(e.key);
    }
  };

  // A timed element reports its position to the follower, as VmplFilePlayer does.
  useEffect(() => {
    if (!timed || !driver) return;
    const id = setInterval(() => {
      const s = media.current.sample();
      if (s) reportRef.current(driver.key, s.positionMs, s.state);
    }, REPORT_MS);
    return () => clearInterval(id);
  }, [timed, driver]);

  // Live: play while the cast plays; a resume reloads, so it joins the live edge again.
  const wasPaused = useRef(false);
  useEffect(() => {
    if (!live) return;
    if (slot.playStatus === "PAUSE") {
      media.current.pause();
      wasPaused.current = true;
      return;
    }
    if (wasPaused.current) media.current.reload();
    wasPaused.current = false;
    media.current.play();
  }, [live, url, slot.playStatus, slot.timelineRev]);

  const exit: ControlButton = { testId: "dock-exit-fullscreen", label: t("exit_fullscreen"), icon: <Minimize aria-hidden="true" />, onClick: fs.exit, start: true };
  const fsSpec: PlayerControlsSpec = fullScreen
    ? fullScreen.spec({ exit, embed: "bandUnder" })
    : { title: slot.streamingUser?.name ?? "", top: [exit], middle: [], embed: "bandUnder" };

  const bound = !!driver && driver.key === url;
  return (
    <div data-testid="vmpl-link-player" data-kind={kind.id} style={fill ? { width: "100%", height: "100%" } : undefined}>
      {!fill && slot.streamingUser && (
        <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 4 }}>
          {slot.streamingUser.name}
        </div>
      )}
      <PlayableMedia
        item={item}
        resolved={resolved}
        handleRef={media}
        onEvent={onEvent}
        startMs={() => {
          const tl = timelineOf(slotRef.current);
          return tl ? positionAt(tl, serverClock.now()) : 0;
        }}
        // Silent too long while the cast plays → the browser blocked autoplay. The wait starts at `loaded` (before that
        // the follower has no driver and a tap could do nothing) and after the scheduled start.
        shouldPlay={timed && bound && slot.playStatus === "PLAY" && !heldEnd}
        silenceIsBlocked
        blockedKey={`${url}|${slot.seekTime}|${slot.playStartTime}|${slot.timelineRev}`}
        blockedDelayMs={() => {
          const tl = timelineOf(slotRef.current);
          return tl?.status === "PLAY" && tl.startAt != null ? Math.max(0, tl.startAt - serverClock.now()) : 0;
        }}
        // The tap plays from the synced position (timed); a live stream just plays.
        onTapToListen={timed ? resync : undefined}
        showError
        face
        frame={{
          ref: setFrameEl,
          testId: "link-player-frame",
          className: "group",
          style: {
            position: "relative",
            width: "100%",
            ...(fill ? { height: "100%" } : { aspectRatio: "16/9" }),
            borderRadius: 4,
            overflow: "hidden",
            background: "#000",
          },
          wrap: (m) => (
            <FullScreenPlayerControls spec={fsSpec} visibility={vis} on={isFullscreen}>
              {m}
            </FullScreenPlayerControls>
          ),
          extra: (
            <>
              {isFullscreen && fullScreen?.dialog}
              {/* The enter button (a kind with a picture worth it; SoundCloud's widget is not); in full screen the
                  band has the one exit. */}
              {kind.caps.fullScreen && !failed && !isFullscreen && (
                <button
                  type="button"
                  data-testid="btn-fullscreen"
                  onClick={fs.toggle}
                  title={t("fullscreen")}
                  aria-label={t("fullscreen")}
                  className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity"
                  style={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    zIndex: 2,
                    width: 24,
                    height: 24,
                    borderRadius: 4,
                    background: "rgba(0,0,0,0.5)",
                    border: "none",
                    color: "#fff",
                    cursor: "pointer",
                    fontSize: 12,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  ⛶
                </button>
              )}
            </>
          ),
        }}
      />
    </div>
  );
}
